import React, { useState, useEffect } from 'react';
import { useNavigate, Outlet, Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import { 
  Shield, LayoutDashboard, Building2, Users, LogOut, 
  ChevronRight, Menu, X, Settings, Search, Bell, FileText, Crown, Activity, Users2, CreditCard, Paintbrush
} from 'lucide-react';
import { cn } from '@/lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const navItems = [
  { path: '/tenant-super/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/tenant-super/tenants', label: 'Empresas', icon: Building2 },
  { path: '/tenant-super/plans', label: 'Planos', icon: Crown },
  { path: '/tenant-super/billing', label: 'Faturamento', icon: CreditCard },
  { path: '/tenant-super/health', label: 'Saúde', icon: Activity },
  { path: '/tenant-super/all-users', label: 'Usuários', icon: Users2 },
  { path: '/tenant-super/whitelabel', label: 'White-Label', icon: Paintbrush },
  { path: '/tenant-super/admins', label: 'Administradores', icon: Users },
  { path: '/tenant-super/audit', label: 'Audit Logs', icon: FileText },
  { path: '/tenant-super/settings', label: 'Configurações', icon: Settings },
];

function useSuperAdmin() {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('super_admin_token');
    if (!token) {
      navigate('/tenant-super');
      return;
    }

    const fetchAdmin = async () => {
      try {
        const response = await axios.get(`${API}/super/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAdmin(response.data.admin);
      } catch (err) {
        localStorage.removeItem('super_admin_token');
        localStorage.removeItem('super_admin');
        navigate('/tenant-super');
      } finally {
        setLoading(false);
      }
    };

    fetchAdmin();
  }, [navigate]);

  return { admin, loading };
}

export function getSuperAdminHeader() {
  const token = localStorage.getItem('super_admin_token');
  return { Authorization: `Bearer ${token}` };
}

export function SuperAdminLayout() {
  const { admin, loading } = useSuperAdmin();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();

  // Force light theme for SuperAdmin
  useEffect(() => {
    const root = document.documentElement;
    const wasDark = root.classList.contains('dark');
    root.classList.remove('dark');
    root.classList.add('light');
    return () => {
      // Restore previous theme on unmount
      if (wasDark) {
        root.classList.remove('light');
        root.classList.add('dark');
      }
    };
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('super_admin_token');
    localStorage.removeItem('super_admin');
    navigate('/tenant-super');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin h-8 w-8 border-4 border-indigo-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex light" data-theme="light">
      {/* Dark Sidebar */}
      <aside className={cn(
        "fixed inset-y-0 left-0 z-50 flex flex-col bg-slate-900 transition-all duration-300",
        sidebarOpen ? "w-64" : "w-20"
      )}>
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-slate-800">
          {sidebarOpen && (
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-lg bg-indigo-600 flex items-center justify-center">
                <Shield className="h-5 w-5 text-white" />
              </div>
              <span className="font-bold text-lg text-white">OneView</span>
            </div>
          )}
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="text-slate-400 hover:text-white hover:bg-slate-800"
          >
            {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
        </div>

        {/* Navigation */}
        <ScrollArea className="flex-1 py-4">
          <nav className="space-y-1 px-3">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all",
                    isActive 
                      ? "bg-indigo-600 text-white shadow-lg shadow-indigo-600/30" 
                      : "text-slate-400 hover:text-white hover:bg-slate-800"
                  )}
                  data-testid={`super-nav-${item.label.toLowerCase()}`}
                >
                  <Icon className="h-5 w-5 flex-shrink-0" />
                  {sidebarOpen && <span className="font-medium">{item.label}</span>}
                  {sidebarOpen && isActive && <ChevronRight className="ml-auto h-4 w-4" />}
                </Link>
              );
            })}
          </nav>
        </ScrollArea>

        {/* User & Logout */}
        <div className="p-4 border-t border-slate-800">
          {sidebarOpen && admin && (
            <div className="mb-3 px-2">
              <p className="text-sm font-medium text-white truncate">{admin.full_name}</p>
              <p className="text-xs text-slate-400 truncate">{admin.email}</p>
            </div>
          )}
          <Button 
            variant="ghost" 
            className={cn(
              "w-full text-red-400 hover:text-red-300 hover:bg-red-900/20",
              !sidebarOpen && "px-0 justify-center"
            )}
            onClick={handleLogout}
            data-testid="super-admin-logout"
          >
            <LogOut className="h-5 w-5" />
            {sidebarOpen && <span className="ml-2">Sair</span>}
          </Button>
        </div>
      </aside>

      {/* Main Content - Light Theme */}
      <main className={cn(
        "flex-1 transition-all duration-300",
        sidebarOpen ? "ml-64" : "ml-20"
      )}>
        {/* Top Header */}
        <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-6 sticky top-0 z-40">
          <div className="flex items-center gap-4">
            <h1 className="text-lg font-semibold text-gray-900">
              {navItems.find(n => n.path === location.pathname)?.label || 'Super Admin'}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input 
                placeholder="Buscar..."
                className="w-64 pl-9 bg-gray-50 border-gray-200 focus:bg-white"
              />
            </div>
            <Button variant="ghost" size="icon" className="text-gray-500">
              <Bell className="h-5 w-5" />
            </Button>
          </div>
        </header>

        {/* Page Content */}
        <div className="p-6">
          <Outlet context={{ admin }} />
        </div>
      </main>
    </div>
  );
}
